/**
 * Per-account Djay variant selection hook.
 *
 * Scopes the selected mascotte variant to the active learner account.
 * When switching accounts, each account gets their own variant (or defaults to 'default').
 *
 * DDD co-locatie: moved from components/mascot/ in s60.
 * Fase 2 dataLayer migration: localStorage calls replaced by dataLayer.
 *
 * REACTIVITY FIX: Uses a module-level store with listeners so ALL consumers
 * of useDjaySelection() stay in sync when any one of them changes the variant.
 *
 * Audit 2026: dubbele effect (mount + prev-ref) samengevoegd tot één
 * effect met [accountId] deps (H8). Zie docs/audits/MASCOT_DOMAIN_AUDIT_2026.md §3.1.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { DjayVariant, DjayAccessory, DjayTier } from './DjayAvatar'
import { useAppStore } from '../app/hooks/useAppStore'
import { dataLayer } from '../data/dataLayer'

const DEFAULT_VARIANT: DjayVariant = 'default'
const DEFAULT_ACCESSORY: DjayAccessory = 'none'
const DEFAULT_TIER: DjayTier = 'rookie'

// ── Module-level shared store ────────────────────────────────────────
// All useDjaySelection() instances subscribe to this store so changes
// in FloatingDjay are instantly reflected in ProgressPage and vice versa.

type Listener = () => void

interface SelectionState {
  variant: DjayVariant
  accessory: DjayAccessory
  tier: DjayTier
}

let _currentState: SelectionState = {
  variant: DEFAULT_VARIANT,
  accessory: DEFAULT_ACCESSORY,
  tier: DEFAULT_TIER,
}
let _currentAccountId: string | null = null
/**
 * Monotonic generation token. Bumped on every account-switch load AND
 * on every explicit write. An in-flight load only applies
 * its result if the generation hasn't moved — this guards against the
 * "user picks variant before initial load resolves" race that would
 * otherwise overwrite the user's choice with the stored value.
 * (Audit RC ronde 2 2026.)
 */
let _generation = 0
const _listeners = new Set<Listener>()

function getSnapshot(): SelectionState {
  return _currentState
}

function subscribe(listener: Listener): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

function _notify() {
  _listeners.forEach((l) => l())
}

function _setVariant(variant: DjayVariant, accountId: string | null) {
  _generation += 1
  _currentState = { ..._currentState, variant }
  _notify()
  void dataLayer.saveDjayVariant(
    accountId,
    variant === DEFAULT_VARIANT ? null : variant,
  )
}

function _setAccessory(accessory: DjayAccessory, accountId: string | null) {
  _generation += 1
  _currentState = { ..._currentState, accessory }
  _notify()
  void dataLayer.saveDjayAccessory(
    accountId,
    accessory === DEFAULT_ACCESSORY ? null : accessory,
  )
}

function _setTier(tier: DjayTier, accountId: string | null) {
  _generation += 1
  _currentState = { ..._currentState, tier }
  _notify()
  void dataLayer.saveDjayTier(accountId, tier === DEFAULT_TIER ? null : tier)
}

async function _loadSelectionForAccount(accountId: string | null) {
  _generation += 1
  const gen = _generation
  _currentAccountId = accountId

  const [storedVariant, storedAccessory, storedTier] = await Promise.all([
    dataLayer.getDjayVariant(accountId),
    dataLayer.getDjayAccessory(accountId),
    dataLayer.getDjayTier(accountId),
  ])

  // Apply only if neither (a) the account changed nor (b) a write
  // happened during the await — both bump the generation.
  if (_currentAccountId === accountId && _generation === gen) {
    _currentState = {
      variant: (storedVariant as DjayVariant) || DEFAULT_VARIANT,
      accessory: (storedAccessory as DjayAccessory) || DEFAULT_ACCESSORY,
      tier: (storedTier as DjayTier) || DEFAULT_TIER,
    }
    _notify()
  }
}

// ── Public hook ──────────────────────────────────────────────────────

export function useDjaySelection() {
  const { activeAccount } = useAppStore()
  const accountId = activeAccount?.id ?? null

  // Single effect: load on mount AND on account-switch.
  useEffect(() => {
    _loadSelectionForAccount(accountId)
  }, [accountId])

  // Subscribe to the shared store
  const state = useSyncExternalStore(subscribe, getSnapshot)

  const setSelectedVariant = useCallback(
    (variant: DjayVariant) => {
      _setVariant(variant, accountId)
    },
    [accountId],
  )

  const setSelectedAccessory = useCallback(
    (accessory: DjayAccessory) => {
      _setAccessory(accessory, accountId)
    },
    [accountId],
  )

  const setSelectedTier = useCallback(
    (tier: DjayTier) => {
      _setTier(tier, accountId)
    },
    [accountId],
  )

  return {
    selectedVariant: state.variant,
    setSelectedVariant,
    selectedAccessory: state.accessory,
    setSelectedAccessory,
    selectedTier: state.tier,
    setSelectedTier,
  }
}
